import { type PollAnswerValue, type PollField, isAnswerableFieldType } from './api';

const BRANCH_TYPES = new Set(['single_choice', 'dropdown']);

export const visiblePollFieldIds = (
  fields: readonly PollField[],
  values: Readonly<Record<string, PollAnswerValue | undefined>>,
): Set<string> => {
  const blocks: PollField[][] = [];
  for (const field of fields) {
    if (field.type === 'section' || blocks.length === 0) blocks.push([]);
    blocks[blocks.length - 1]?.push(field);
  }
  const sections = new Map<string, number>();
  for (const [index, block] of blocks.entries()) {
    const section = block.find((field) => field.type === 'section');
    if (section) sections.set(section.id, index);
  }

  const visible = new Set<string>();
  let index = 0;
  while (index < blocks.length) {
    const block = blocks[index];
    if (!block) break;
    for (const field of block) visible.add(field.id);
    let jump: string | null = null;
    for (const field of block) {
      if (!BRANCH_TYPES.has(field.type)) continue;
      const value = values[field.id];
      if (typeof value !== 'string') continue;
      jump = field.options.find((option) => option.id === value)?.jumpToSectionId ?? null;
      if (jump !== null) break;
    }
    if (jump === 'submit') break;
    index = jump ? (sections.get(jump) ?? blocks.length) : index + 1;
  }
  return visible;
};

const empty = (value: PollAnswerValue | undefined) =>
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === 'object' &&
    !Array.isArray(value) &&
    (!String(value.start).trim() || !String(value.end).trim()));

export const validatePollResponse = (
  fields: readonly PollField[],
  values: Readonly<Record<string, PollAnswerValue | undefined>>,
): Record<string, string> => {
  const errors: Record<string, string> = {};
  const visible = visiblePollFieldIds(fields, values);
  for (const field of fields) {
    if (!visible.has(field.id) || !isAnswerableFieldType(field.type)) continue;
    const value = values[field.id];
    if (field.required && empty(value)) {
      errors[field.id] = 'Required';
      continue;
    }
    if (empty(value)) continue;
    if (field.type === 'multi_choice' && Array.isArray(value)) {
      if (value.length < (field.config.minSelections ?? 0))
        errors[field.id] = 'Select more options';
      if (value.length > (field.config.maxSelections ?? Number.POSITIVE_INFINITY)) {
        errors[field.id] = 'Select fewer options';
      }
    } else if ((field.type === 'number' || field.type === 'scale') && typeof value === 'number') {
      if (field.config.min !== undefined && value < field.config.min)
        errors[field.id] = `Minimum ${field.config.min}`;
      if (field.config.max !== undefined && value > field.config.max)
        errors[field.id] = `Maximum ${field.config.max}`;
    } else if (
      (field.type === 'short_text' || field.type === 'long_text') &&
      typeof value === 'string'
    ) {
      if (field.config.maxLength !== undefined && value.length > field.config.maxLength) {
        errors[field.id] = `Maximum ${field.config.maxLength} characters`;
      }
    } else if (
      (field.type === 'number_range' || field.type === 'date_range') &&
      typeof value === 'object' &&
      !Array.isArray(value)
    ) {
      if (
        field.type === 'date_range' &&
        typeof value.start === 'string' &&
        typeof value.end === 'string'
      ) {
        if (field.config.minDate && value.start < field.config.minDate)
          errors[field.id] = `Minimum date ${field.config.minDate}`;
        if (field.config.maxDate && value.end > field.config.maxDate)
          errors[field.id] = `Maximum date ${field.config.maxDate}`;
      }
      if (value.start > value.end) errors[field.id] = 'Start must not exceed end';
      if (
        field.type === 'number_range' &&
        typeof value.start === 'number' &&
        typeof value.end === 'number'
      ) {
        if (field.config.min !== undefined && value.start < field.config.min)
          errors[field.id] = `Minimum ${field.config.min}`;
        if (field.config.max !== undefined && value.end > field.config.max)
          errors[field.id] = `Maximum ${field.config.max}`;
      }
    }
  }
  return errors;
};
